import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';
import { LazyLoadImage } from '@components/reactLazyLoadImageComponent';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import { observer } from 'mobx-react';
import { useKeenSlider } from 'keen-slider/react';
import { twcls } from '@utils/twcls';
import './index.scss';

const { productCardErrorImg } = UIicons;

const BIG_IMG_SLIDE__CLS =
  'allMd:!h-[192px] allMd:!max-h-[192px] allMd:!min-h-[192px]';

const onImgError = createImageErrorHandler(productCardErrorImg.x3);

interface MobileProductImgWrapperProps {
  imgsArr: string[];
  title: string;
  onImgHover: () => void;
  omImgHoverEnd: () => void;
  InStoreBlockComp: ReactNode;
  productNotInCart?: boolean;
  currentSlide: number;
  isShowControllers: boolean;
  setCurrentSlide: (value: number) => void;
  isBigImg?: boolean;
}

const eventListener = (e): void => {
  e.stopPropagation();
};

const MobileProductImgWrapper: FC<MobileProductImgWrapperProps> = observer(
  ({
    imgsArr,
    productNotInCart,
    title,
    InStoreBlockComp,
    omImgHoverEnd,
    isShowControllers,
    currentSlide,
    setCurrentSlide,
    isBigImg
  }) => {
    const [sliderRef, instanceRef] = useKeenSlider({
      disabled: imgsArr?.length === 1,
      selector: '.product-card-image-slide',
      loop: true,
      slides: {
        perView: 1,
        spacing: 0
      },
      created(slider) {
        setTimeout(() => {
          slider?.update();
        }, 300);
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      dragStarted(slider) {
        slider.container.addEventListener('touchstart', eventListener, {
          once: true
        });
        slider.container.addEventListener('mousedown', eventListener, {
          once: true
        });
      },
      dragEnded(slider) {
        slider.container.removeEventListener('touchstart', eventListener);
        slider.container.removeEventListener('mousedown', eventListener);
      }
    });

    const onMouseMove = useCallback(
      e => {
        const imgRect = instanceRef.current?.container?.getBoundingClientRect();

        if (
          isShowControllers &&
          instanceRef &&
          imgRect?.x &&
          productNotInCart
        ) {
          const cursorX = Math.abs(e.clientX - imgRect.x);
          const newImgInd = Math.floor(
            imgsArr.length * (cursorX / imgRect.width)
          );
          if (currentSlide !== newImgInd) {
            instanceRef.current?.moveToIdx(newImgInd);
            setCurrentSlide(newImgInd);
          }
        }
      },
      [
        instanceRef,
        isShowControllers,
        productNotInCart,
        imgsArr.length,
        currentSlide,
        setCurrentSlide
      ]
    );

    const handleEnd = useCallback(() => {
      if (omImgHoverEnd && isShowControllers && productNotInCart) {
        omImgHoverEnd();
      }
      if (isShowControllers && productNotInCart) {
        if (currentSlide !== 0) {
          instanceRef.current?.moveToIdx(0);
          setCurrentSlide(0);
        }
        document.body.style.removeProperty('overflow');
      }
    }, [
      omImgHoverEnd,
      isShowControllers,
      productNotInCart,
      currentSlide,
      instanceRef,
      setCurrentSlide
    ]);

    return (
      <div
        ref={sliderRef}
        className='keen-slider MobileProductImgWrapper'
        onMouseMove={onMouseMove}
        onMouseLeave={handleEnd}
      >
        {!!imgsArr?.length &&
          imgsArr.map((item, index) => (
            <div
              key={item}
              className={twcls(
                'product-card-image-slide ProductImgWrapper__img-wrapper',
                isBigImg && BIG_IMG_SLIDE__CLS
              )}
            >
              {InStoreBlockComp && InStoreBlockComp}
              <LazyLoadImage
                draggable={false}
                onError={onImgError}
                visibleByDefault={index === 1}
                className='ProductImgWrapper__img'
                src={item || productCardErrorImg.x3}
                alt={title}
                width={158}
                height={140}
              />
            </div>
          ))}
      </div>
    );
  }
);

export default MobileProductImgWrapper;
